import { useState } from 'react'
import { Chat } from '@/config'
import { modal, alert } from '@codeleap/modals'
import { ChatLocalMesage } from '@packages/chat'
import { createStyles } from '@codeleap/styles'
import { View, Button, TimeInput, DateInput } from '@/components'

export type SetReminderModalProps = {
  message?: ChatLocalMesage
}

export const SetReminderModal = modal<SetReminderModalProps>({ id: 'set-reminder-modal' }).content(props => {
  const { message } = props
  const [dateValue, setDateValue] = useState<string>('')
  const [timeValue, setTimeValue] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const minimumDate = new Date(Date.now() + 2 * 60 * 1000)
  const minDateString = getMinDateString(minimumDate)

  const isFormValid = !!dateValue && !!timeValue

  const handleSave = async () => {
    if (!message || !dateValue || !timeValue) return

    const [year, month, day] = dateValue.split('-').map(Number)
    const [hours, minutes] = timeValue.split(':').map(Number)

    const dateObj = new Date(year, month - 1, day)
    const timeObj = new Date()
    timeObj.setHours(hours, minutes, 0, 0)
    const combinedDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0)
    const now = new Date()
    const oneMinutesFromNow = new Date(now.getTime() + 1 * 60 * 1000)

    if (combinedDateTime < oneMinutesFromNow) {
      alert.error({
        title: 'Invalid reminder time',
        body: 'The reminder time must be at least 2 minutes from now.',
      })
      return
    }

    setLoading(true)
    try {
      await Chat.Services.createReminder({
        messageId: message.id,
        date: dateObj,
        time: timeObj,
        onFinally: () => {
          setLoading(false)
          props?.toggle?.()
          SetReminderModal.close()
        },
      })
    } catch (e) {
      // @ts-ignore
      const errorMessage = e?.message
      // Extract error message from quotes if present
      const match = errorMessage?.match(/"([^"]*)"/)
      const finalErrorMessage = match ? match[1] : (errorMessage || `Error while trying to create the reminder`)

      alert.error({
        title: `Error`,
        body: finalErrorMessage,
      })
    }
  }

  SetReminderModal.useProps({
    title: 'Set reminder',
    style: ['center', 'box:noPadding', { titleWrapper: styles.titleWrapper, box: styles.box }],
    toggle: () => { },
  })

  return (
    <View style={styles.wrapper}>
      <View style={styles.content}>
        <DateInput
          value={dateValue}
          onValueChange={setDateValue}
          style={['fullWidth', 'noError']}
          debugName='SetReminder:DateInput'
          min={minDateString}
        />

        <TimeInput
          value={timeValue}
          onValueChange={setTimeValue}
          style={['fullWidth', 'noError']}
          debugName='SetReminder:TimeInput'
          min={minDateString}
        />

        <Button
          loading={loading}
          debugName='SetReminder:Save'
          text={loading ? null : 'Save'}
          style={['large', 'fullWidth']}
          disabled={!isFormValid}
          onPress={handleSave}
        />
      </View>
    </View>
  )
})

function getMinDateString(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const styles = createStyles(theme => ({
  titleWrapper: {
    ...theme.spacing.padding(2),
  },
  box: {
    maxWidth: 400,
    width: '100%',
    [theme.media.down('tabletSmall')]: {
      maxWidth: `calc(100% - ${theme.spacing.value(4)}px)`,
    },
  },
  wrapper: {
    ...theme.presets.column,
    width: '100%',
  },
  content: {
    ...theme.presets.column,
    ...theme.spacing.gap(2),
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.paddingBottom(2),
  },
}))
